import {
  MOMENCE_HOST_ID,
  MOMENCE_TOKEN,
  MOMENCE_API_BASE,
  MOMENCE_SCHEDULE_URL,
  MOMENCE_STUDIO_TIMEZONE,
} from "./momence-config.js";
import { setEmbedUrl, revealEmbed } from "./momence-embed.js";

let selectedEventId = null;
let selectionBound = false;

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function displayClassName(event) {
  const description = (event.description || "").trim();
  return description || event.title;
}

function formatWhen(dateTime, durationMinutes) {
  const start = new Date(dateTime);
  const duration = durationMinutes || 60;

  const datePart = start.toLocaleString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: MOMENCE_STUDIO_TIMEZONE,
  });

  const startTime = start.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: MOMENCE_STUDIO_TIMEZONE,
    timeZoneName: "short",
  });

  return {
    datePart,
    startTime,
    durationLabel: `${duration} minutes`,
  };
}

function formatAvailability(event) {
  if (event.isCancelled) return "Cancelled";
  if (event.spotsRemaining === null) return "Open";
  if (event.spotsRemaining <= 0) return "Full";
  return `${event.spotsRemaining} spots left`;
}

function renderClassListItem(event, isSelected) {
  const className = displayClassName(event);
  const { datePart, startTime, durationLabel } = formatWhen(
    event.dateTime,
    event.duration
  );
  const availability = formatAvailability(event);
  const selectedClass = isSelected ? " momence-class-item--selected" : "";
  const full = event.spotsRemaining !== null && event.spotsRemaining <= 0;

  return `
    <button
      type="button"
      class="momence-class-item${selectedClass}${full ? " momence-class-item--full" : ""}"
      data-momence-url="${escapeHtml(event.link)}"
      data-event-id="${event.id}"
      ${full ? 'aria-disabled="true"' : ""}
      aria-pressed="${isSelected ? "true" : "false"}"
    >
      <span class="momence-class-item__main">
        <span class="momence-class-item__name">${escapeHtml(className)}</span>
        <span class="momence-class-item__when">${escapeHtml(datePart)} · ${escapeHtml(startTime)}</span>
        <span class="momence-class-item__duration">${escapeHtml(durationLabel)}${event.teacher ? ` · ${escapeHtml(event.teacher)}` : ""}</span>
      </span>
      <span class="momence-class-item__meta">
        <span class="momence-class-item__spots">${escapeHtml(availability)}</span>
      </span>
    </button>
  `;
}

function updateStudioLocation(location, teacher, classCount) {
  const footerLocation = document.getElementById("studio-location");
  const scheduleLocation = document.getElementById("momence-location");
  const eventCount = document.getElementById("momence-event-count");

  if (location && footerLocation) {
    footerLocation.textContent = location;
  }

  if (scheduleLocation) {
    const parts = [];
    if (location) {
      parts.push(`Classes held at ${location}`);
    }
    if (teacher) {
      parts.push(`with ${teacher}`);
    }
    scheduleLocation.textContent = parts.join(" · ") || "Upcoming classes from YOQIGA";
  }

  if (eventCount && classCount > 0) {
    eventCount.textContent = `${classCount} upcoming class${classCount === 1 ? "" : "es"} — tap one to load checkout`;
  }
}

function updateFallbackLink(url) {
  const fallback = document.getElementById("momence-embed-fallback");
  if (fallback && url) {
    fallback.href = url;
  }
}

function resetCheckout(message) {
  const frameWrap = document.getElementById("momence-embed-frame-wrap");
  const placeholder = document.getElementById("momence-embed-placeholder");

  if (frameWrap) {
    frameWrap.hidden = true;
  }

  if (placeholder) {
    placeholder.hidden = false;
    placeholder.textContent =
      message || "Select a class above to load checkout.";
  }

  selectedEventId = null;
}

function scrollToCheckout() {
  const checkout = document.getElementById("booking-checkout");
  if (checkout) {
    checkout.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function selectClass(eventId, url, { userAction = false } = {}) {
  if (!url) return;

  selectedEventId = eventId;
  setEmbedUrl(url);
  revealEmbed(url);
  updateFallbackLink(url);

  document.querySelectorAll(".momence-class-item").forEach((el) => {
    const isSelected = el.getAttribute("data-event-id") === String(eventId);
    el.classList.toggle("momence-class-item--selected", isSelected);
    el.setAttribute("aria-pressed", isSelected ? "true" : "false");
  });

  if (userAction) {
    scrollToCheckout();
  }
}

function bindClassSelection() {
  if (selectionBound) return;
  selectionBound = true;

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest(".momence-class-item[data-momence-url]");
    if (!trigger || trigger.classList.contains("momence-class-item--full")) {
      return;
    }

    selectClass(
      trigger.getAttribute("data-event-id"),
      trigger.getAttribute("data-momence-url"),
      { userAction: true }
    );
  });
}

function renderEmpty(message) {
  const classList = document.getElementById("momence-class-list");
  const eventCount = document.getElementById("momence-event-count");

  if (classList) {
    classList.innerHTML = `<p class="momence-class-list__empty mb-0">${escapeHtml(message)}</p>`;
  }

  if (eventCount) {
    eventCount.textContent = message;
  }
}

async function fetchEvents(endpoint, retries = 2) {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`Momence API error: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  }

  throw lastError;
}

function renderError() {
  const message = "Unable to load classes right now.";
  const fallback = `<a href="${escapeHtml(MOMENCE_SCHEDULE_URL)}" target="_blank" rel="noopener noreferrer">Open Momence ↗</a>`;
  const classList = document.getElementById("momence-class-list");
  const placeholder = document.getElementById("momence-embed-placeholder");

  if (classList) {
    classList.innerHTML = `<p class="momence-class-list__empty mb-0">${message} ${fallback}</p>`;
  }

  if (placeholder) {
    placeholder.innerHTML = `${message} ${fallback}`;
    placeholder.hidden = false;
  }
}

function renderSchedule(upcoming) {
  const classList = document.getElementById("momence-class-list");

  const firstBookable =
    upcoming.find((e) => e.spotsRemaining === null || e.spotsRemaining > 0) ||
    upcoming[0];

  const preserved =
    selectedEventId != null
      ? upcoming.find((e) => String(e.id) === String(selectedEventId))
      : null;

  const active = preserved || firstBookable;

  if (classList) {
    classList.innerHTML = upcoming
      .map((event) =>
        renderClassListItem(event, active && event.id === active.id)
      )
      .join("");
  }

  if (active) {
    selectClass(active.id, active.link, { userAction: false });
  }
}

export async function initMomenceEvents() {
  const classList = document.getElementById("momence-class-list");
  if (!classList) return;

  bindClassSelection();

  try {
    const endpoint = `${MOMENCE_API_BASE}/Events?hostId=${MOMENCE_HOST_ID}&token=${MOMENCE_TOKEN}`;
    const events = await fetchEvents(endpoint);
    const now = Date.now();

    const upcoming = events
      .filter((event) => event.published && !event.isCancelled && !event.isDeleted)
      .filter((event) => new Date(event.dateTime).getTime() >= now)
      .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));

    if (upcoming.length) {
      const first = upcoming[0];
      updateStudioLocation(first.location, first.teacher, upcoming.length);
      renderSchedule(upcoming);
    } else {
      updateStudioLocation("Center Stage Dance Studio", null, 0);
      renderEmpty("No upcoming classes scheduled. Check back soon!");
      updateFallbackLink(MOMENCE_SCHEDULE_URL);
      resetCheckout("No classes available to book right now.");
    }
  } catch (error) {
    console.error(error);
    renderError();
    updateFallbackLink(MOMENCE_SCHEDULE_URL);
    resetCheckout("Unable to load checkout. Select a class when the schedule returns.");
  }
}

export function startMomenceScheduleRefresh(intervalMs = 5 * 60 * 1000) {
  setInterval(() => {
    initMomenceEvents();
  }, intervalMs);
}
